import type { ComponentType, SVGProps } from 'react';
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowUturnLeftIcon,
  ComputerDesktopIcon,
  KeyIcon,
  PlusCircleIcon,
  UserMinusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { ROLE_SI_RH } from '@/contexts/AuthContext';
import type { Solicitacao, StatusSolicitacao, TipoSolicitacao } from '@/features/SI/models';
import { textoCorresponde } from '@/features/SI/utils/buscaTexto';
import { TIPO_SOLICITACAO_LABELS } from '@/features/SI/pages/NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_SOLICITACAO_LABELS } from '@/features/SI/pages/SolicitacoesListPage/solicitacoesListPage.definitions';

export interface SiSolicitacaoCard {
  tipo: TipoSolicitacao;
  titulo: string;
  descricao: string;
  nota?: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Sem `roles`, todos veem. Mesma regra do formulário (NovaSolicitacaoContent:
   * Admissão/Desligamento só `si_rh`) — esconder aqui é só UX, a barreira é o firestore.rules. */
  roles?: readonly string[];
}

/** Os cartões substituem o "Tipo de solicitação" como 1º passo: clicar abre o mesmo
 * formulário de hoje já com o tipo escolhido. "Formatação" fica de fora de propósito — só
 * nasce pelo wizard de Alocações (ver NovaSolicitacaoContent). */
export const SI_SOLICITACAO_CARDS: readonly SiSolicitacaoCard[] = [
  { tipo: 'troca_equipamento', titulo: 'Troca de equipamento', descricao: 'Defeito, dano ou exigência do projeto.', icon: ArrowPathIcon },
  {
    tipo: 'nova_demanda',
    titulo: 'Nova alocação',
    descricao: 'Equipamento para um novo projeto, mantendo os atuais.',
    icon: PlusCircleIcon,
  },
  {
    tipo: 'mudanca_projeto',
    titulo: 'Mudança de projeto',
    descricao: 'Envio para o novo projeto e devolução do anterior.',
    icon: ArrowsRightLeftIcon,
  },
  { tipo: 'devolucao', titulo: 'Devolução', descricao: 'Fim de projeto ou equipamento sem uso.', icon: ArrowUturnLeftIcon },
  { tipo: 'acessos', titulo: 'Acessos', descricao: 'HelpDesk, RaroHub, GitLab, Microsoft.', icon: KeyIcon },
  {
    tipo: 'pedido_colaborador',
    titulo: 'Periféricos e acessórios',
    descricao: 'Monitor, smartphone, chip, cartão de acesso, mouse, headset...',
    nota: 'Sujeito à disponibilidade',
    icon: ComputerDesktopIcon,
  },
  {
    tipo: 'admissao',
    titulo: 'Admissão',
    descricao: 'Equipamentos e acessos da pessoa nova.',
    icon: UserPlusIcon,
    roles: [ROLE_SI_RH],
  },
  {
    tipo: 'desligamento',
    titulo: 'Desligamento',
    descricao: 'Devolução de tudo e corte de acessos.',
    icon: UserMinusIcon,
    roles: [ROLE_SI_RH],
  },
];

export function getVisibleSiSolicitacaoCards(hasRole: (role: string) => boolean): SiSolicitacaoCard[] {
  return SI_SOLICITACAO_CARDS.filter((card) => !card.roles || card.roles.some((role) => hasRole(role)));
}

/** Abas abaixo dos cartões. "Da minha equipe" só aparece pra quem tem alguma (ver página). */
export type SiSolicitacoesLista = 'minhas' | 'equipe' | 'equipamentos';

export const SI_SOLICITACOES_LISTAS: ReadonlyArray<{ id: SiSolicitacoesLista; label: string }> = [
  { id: 'minhas', label: 'Minhas solicitações' },
  { id: 'equipe', label: 'Da minha equipe' },
  { id: 'equipamentos', label: 'Meus equipamentos' },
];

/** Recorte de status da lista — "Em aberto" é o padrão (mesmo da antiga aba "Que eu abri"). */
export type FiltroStatusSolicitacoes = 'em_aberto' | 'concluida' | 'cancelada' | 'todas';

export const FILTRO_STATUS_OPCOES: ReadonlyArray<{ valor: FiltroStatusSolicitacoes; label: string }> = [
  { valor: 'em_aberto', label: 'Em aberto' },
  { valor: 'concluida', label: 'Concluídas' },
  { valor: 'cancelada', label: 'Canceladas' },
  { valor: 'todas', label: 'Todas' },
];

export interface FiltroSolicitacoes {
  texto: string;
  status: FiltroStatusSolicitacoes;
  tipo: TipoSolicitacao | '';
}

export const FILTRO_SOLICITACOES_PADRAO: FiltroSolicitacoes = { texto: '', status: 'em_aberto', tipo: '' };

function paraMillis(timestamp: Solicitacao['createdAt'] | undefined): number {
  if (!timestamp) return 0;
  return typeof (timestamp as { toMillis?: () => number }).toMillis === 'function'
    ? (timestamp as { toMillis: () => number }).toMillis()
    : new Date(timestamp as unknown as string).getTime();
}

export function dataAberturaMillis(solicitacao: Solicitacao): number {
  return paraMillis(solicitacao.createdAt);
}

function correspondeStatus(status: StatusSolicitacao, filtro: FiltroStatusSolicitacoes): boolean {
  if (filtro === 'todas') return true;
  if (filtro === 'em_aberto') return status === 'aberta' || status === 'em_andamento';
  return status === filtro;
}

/**
 * Aplica o filtro das listas (mais recente primeiro). Excluídas nunca aparecem. A busca usa
 * a mesma regra do Controle e do Inventário (textoCorresponde: ignora acento/pontuação) sobre
 * tipo, colaborador, cliente, status e data de abertura.
 */
export function filtrarSolicitacoes(
  itens: Solicitacao[],
  filtro: FiltroSolicitacoes,
  nomeCliente: (clienteId: string | null | undefined) => string | undefined
): Solicitacao[] {
  return itens
    .filter((s) => s.status !== 'excluida')
    .filter((s) => correspondeStatus(s.status, filtro.status))
    .filter((s) => !filtro.tipo || s.tipoSolicitacao === filtro.tipo)
    .filter((s) => {
      if (!filtro.texto.trim()) return true;
      const abertura = dataAberturaMillis(s);
      return textoCorresponde(
        [
          TIPO_SOLICITACAO_LABELS[s.tipoSolicitacao],
          s.colaboradorNome,
          nomeCliente(s.clienteId),
          STATUS_SOLICITACAO_LABELS[s.status],
          abertura ? new Date(abertura).toLocaleDateString('pt-BR') : null,
        ],
        filtro.texto
      );
    })
    .sort((a, b) => dataAberturaMillis(b) - dataAberturaMillis(a));
}
